import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Add custom blink animation
const blinkStyle = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.2; }
  }
  .blink-animation {
    animation: blink 0.8s ease-in-out infinite;
  }
`;

// Inject style once
if (typeof document !== 'undefined' && !document.getElementById('blink-animation-style')) {
  const style = document.createElement('style');
  style.id = 'blink-animation-style';
  style.textContent = blinkStyle;
  document.head.appendChild(style);
}

// Error boundary for markdown parsing
class MarkdownErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Markdown rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback to plain text if markdown parsing fails
      return (
        <div className="whitespace-pre-wrap">
          {this.props.content}
        </div>
      );
    }

    return this.props.children;
  }
}

// Safe markdown wrapper with error boundary
const SafeMarkdown = ({ content }) => {
  return (
    <MarkdownErrorBoundary content={content}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
    </MarkdownErrorBoundary>
  );
};

export const ReasoningPreview = ({ text, status }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // DEBUG: Log the raw text to see what we're receiving
  console.log('ReasoningPreview text:', JSON.stringify(text));
  console.log('ReasoningPreview lines:', text.split('\n'));

  return (
    <div className="mb-4 flex w-full flex-col gap-2 -mt-3">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-md w-fit ml-2 border border-gray-200 dark:border-gray-700"
      >
        {/* Activity indicator */}
        <div className="flex items-center gap-2">
          {status?.type === "running" && (
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
          )}
          <span className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-mono">
            {status?.type === "running" ? 'Processing' : 'Analysis steps'}
          </span>
        </div>
        {isCollapsed ? <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" /> : <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" />}
      </button>

      {!isCollapsed && (
        <div className="ml-2 border-l border-gray-300 dark:border-gray-600 pl-4 space-y-3 py-2">
          {(() => {
            // Pre-process text into logical blocks
            const lines = text.split('\n').filter(line => line.trim());
            const blocks = [];
            let currentThinkingBlock = null;

            // First pass: collect all completed tool names
            const completedTools = new Set();
            lines.forEach(line => {
              const trimmed = line.trim();
              if (trimmed.startsWith('Completed:')) {
                const toolName = trimmed.replace('Completed: ', '').trim();
                completedTools.add(toolName);
              }
            });

            for (let i = 0; i < lines.length; i++) {
              const trimmedLine = lines[i].trim();

              // Check line type
              const isThinkingStart = trimmedLine.startsWith('💭');
              const isExecuting = trimmedLine.startsWith('Executing:');
              const isCompleted = trimmedLine.startsWith('Completed:');
              const isInitializing = trimmedLine === 'Initializing analysis' || trimmedLine === 'Agent reasoning';

              if (isThinkingStart) {
                // Start a new thinking block
                const thinkingContent = trimmedLine.substring(2).trim(); // Remove "💭 "
                currentThinkingBlock = { type: 'thinking', content: thinkingContent };
                blocks.push(currentThinkingBlock);
              } else if (isExecuting || isCompleted || isInitializing) {
                // Close any open thinking block
                currentThinkingBlock = null;

                // Skip "Executing:" if the same tool has completed
                if (isExecuting) {
                  const toolName = trimmedLine.replace('Executing: ', '').trim();
                  if (completedTools.has(toolName)) {
                    continue; // Skip this executing block
                  }
                }

                // Add tool execution/completion block
                blocks.push({ type: 'tool', line: trimmedLine });
              } else {
                // Regular line - if we're in a thinking block, append to it
                if (currentThinkingBlock) {
                  currentThinkingBlock.content += '\n\n' + trimmedLine;
                } else {
                  // Otherwise, treat as standalone initializing/general update
                  blocks.push({ type: 'general', line: trimmedLine });
                }
              }
            }

            // Render blocks
            return blocks.map((block, index) => {
              if (block.type === 'thinking') {
                return (
                  <div key={index} className="space-y-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                    <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-mono mb-2">
                      Internal reasoning
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-800 dark:prose-headings:text-gray-200 prose-headings:font-semibold prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-code:text-blue-600 dark:prose-code:text-blue-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1 prose-code:rounded">
                      <SafeMarkdown content={block.content} />
                    </div>
                  </div>
                );
              }

              if (block.type === 'tool') {
                const isExecuting = block.line.startsWith('Executing:');
                const isCompleted = block.line.startsWith('Completed:');

                if (isExecuting) {
                  const toolName = block.line.replace('Executing: ', '');
                  return (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <div className="w-1 h-1 bg-blue-500 rounded-full blink-animation" />
                      <span className="font-mono text-gray-600 dark:text-gray-400 blink-animation">→</span>
                      <code className="text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded text-xs font-mono">{toolName}</code>
                    </div>
                  );
                }

                if (isCompleted) {
                  const toolName = block.line.replace('Completed: ', '');
                  return (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <div className="w-1 h-1 bg-green-500 rounded-full" />
                      <span className="font-mono text-gray-600 dark:text-gray-400">✓</span>
                      <code className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded text-xs font-mono">{toolName}</code>
                    </div>
                  );
                }

                // Initializing text
                return (
                  <div key={index} className="text-sm text-gray-500 dark:text-gray-400 italic">
                    {block.line}
                  </div>
                );
              }

              // General updates
              return (
                <div key={index} className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {block.line}
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
};